// A full-page navigation (`window.location.href = ...`) tears down the current
// React tree — and any in-memory react-hot-toast state — before a toast fired
// on the outgoing page can render. Stashing the message in sessionStorage lets
// the destination page's toaster pick it up on mount, so the confirmation
// survives the redirect (mirrors how a Rails `redirect_to ..., notice:` flash
// survives a server-side redirect).
const PENDING_TOAST_STORAGE_KEY = 'ip.pendingToast';
/**
 * Queues a toast to be shown on the next page load (e.g. right before a
 * `window.location.href` redirect). Best-effort: failures (private
 * browsing, storage disabled) are swallowed so they never block navigation.
 */
export function setPendingToast(message, variant = 'success') {
    try {
        const payload = { message, variant };
        sessionStorage.setItem(PENDING_TOAST_STORAGE_KEY, JSON.stringify(payload));
    }
    catch (_a) {
        // Storage unavailable — the confirmation toast is a nice-to-have, not critical.
    }
}
/**
 * Queues a confirmation and reloads the page, so server-rendered state catches up with
 * a change that was made over AJAX. `RootToaster` replays the toast after the load.
 *
 * Use this where the endpoint changes more than the thing the dialog edited — a status,
 * a history row, a banner — and the page was previously refreshed for free by an MVC
 * form submit. Where only one known element goes stale, hide that element instead: a
 * reload is heavier, and it races anything written by a background worker.
 */
export function reloadWithToast(message, variant = 'success') {
    setPendingToast(message, variant);
    window.location.reload();
}
/**
 * Reads and clears any pending toast queued by `setPendingToast`. Returns
 * `null` when there is nothing to show. Call once, on mount, from the
 * component that owns the toaster.
 */
export function consumePendingToast() {
    try {
        const raw = sessionStorage.getItem(PENDING_TOAST_STORAGE_KEY);
        if (!raw)
            return null;
        sessionStorage.removeItem(PENDING_TOAST_STORAGE_KEY);
        return JSON.parse(raw);
    }
    catch (_a) {
        return null;
    }
}
